import { useEffect, useMemo, useRef, useState } from "react";

import { secondsToHHMMSS } from "../../../services/TimeService/TimeService";

export type RVLiveTimeDisplayProps = {
    /** ISO date string marking when the elapsed timer started counting from */
    startTime: string;
};

/**
 * Displays a live, self-updating elapsed duration counting up from `startTime`.
 *
 * The elapsed time is recomputed every second and formatted via `secondsToHHMMSS`, which
 * renders `MM:SS` when the elapsed hours are zero and `HH:MM:SS` otherwise.
 * The internal interval is reset whenever `startTime` changes and is cleared on unmount.
 *
 * @param props - See {@link RVLiveTimeDisplayProps}
 * @returns A span showing the formatted elapsed time since `startTime`
 *
 * @example
 * ```tsx
 * <RVLiveTimeDisplay startTime={task.startedAt} />
 * ```
 */
export function RVLiveTimeDisplay({ startTime }: RVLiveTimeDisplayProps): JSX.Element {
    const [durationSinceStartTime, setDurationSinceStartTime] = useState(0);
    // Interval must be stored in a ref so that it is not re-initialized on every render
    const durationIntervalRef = useRef<NodeJS.Timeout | null>(null);

    const clearDurationInterval = () => {
        if (durationIntervalRef.current) {
            clearInterval(durationIntervalRef.current);
        }
    };

    // Initialize interval for duration updates, prime it to be cleared on component unmount
    useEffect(() => {
        clearDurationInterval();
        setDurationSinceStartTime((+new Date() - +new Date(startTime)) / 1000);

        const interval = setInterval(() => {
            setDurationSinceStartTime((+new Date() - +new Date(startTime)) / 1000);
        }, 1000);

        durationIntervalRef.current = interval;

        return clearDurationInterval;
    }, [startTime]); // durationIntervalRef.current must be reassigned when the startTime changes after a call wraps

    const formattedTime = useMemo(() => secondsToHHMMSS(durationSinceStartTime), [durationSinceStartTime]);

    return <span data-testid="live-time-display">{formattedTime}</span>;
}
